import { SYMBOL_SOURCE_STATUS_LABELS } from '@/models';
import type { SymbolSourceRef } from '@/models';
import type { EODHDInstrument, VerifySymbolEODHDResult, VerifySymbolSourceReason, VerifySymbolSourceResult } from '@/modelsAPI';
import { formatDate } from '@/utils/helpers';

export type SymbolPriceSource = 'GSHEETS' | 'EODHD';

export const SYMBOL_PRICE_SOURCE_LABELS: Record<SymbolPriceSource, string> = {
  GSHEETS: 'Google Sheets',
  EODHD: 'EODHD',
};

export const SYMBOL_PRICE_SOURCE_SHORT_LABELS: Record<SymbolPriceSource, string> = {
  GSHEETS: 'GS',
  EODHD: 'EODHD',
};

export const GOOGLE_SHEETS_SKIPPED_HINT = 'Wird nicht abgefragt, solange EODHD live liefert.';

// Same patterns the backend validates refs against (after trim + uppercase)
const REF_PATTERNS: Record<SymbolPriceSource, RegExp> = {
  GSHEETS: /^[A-Z0-9]+:[A-Z0-9.-]+$/,
  EODHD: /^[A-Z0-9][A-Z0-9_\-.]*\.[A-Z0-9]+$/,
};

const REF_FORMAT_HINTS: Record<SymbolPriceSource, string> = {
  GSHEETS: 'BÖRSE:TICKER, z.B. ETR:SAP',
  EODHD: 'TICKER.BÖRSE, z.B. SAP.XETRA',
};

export const VERIFY_SOURCE_REASON_LABELS: Record<VerifySymbolSourceReason, string> = {
  FOUND_LIVE: 'gefunden',
  FOUND_CACHE: 'gefunden (Cache)',
  MISSING_LIVE: 'nicht gefunden',
  MISSING_CACHE: 'nicht gefunden (Cache)',
  NO_REF: 'keine Referenz ableitbar',
  INVALID_REF: 'ungültiges Format',
  ERROR: 'Fehler bei der Abfrage',
};

export function normalizeSourceRef(value: string): string {
  return value.trim().toUpperCase();
}

export function isValidSourceRef(source: SymbolPriceSource, value: string): boolean {
  return REF_PATTERNS[source].test(normalizeSourceRef(value));
}

/** Tooltip with everything a compact source badge cannot show inline. */
export function symbolSourceTooltip(label: string, source: SymbolSourceRef): string {
  const parts = [`${label}: ${source.ref ?? 'keine Referenz'}`];
  parts.push(`Status: ${SYMBOL_SOURCE_STATUS_LABELS[source.status] ?? source.status}`);
  parts.push(`Zuletzt geprüft: ${source.lastChecked !== null ? formatDate(source.lastChecked) : 'nie'}`);
  parts.push(`Zuletzt erfolgreich: ${source.lastSuccess !== null ? formatDate(source.lastSuccess) : 'nie'}`);
  if (source.lastError !== undefined && source.lastError !== null && source.lastError !== '') {
    parts.push(`Fehler: ${source.lastError}`);
  }
  return parts.join('\n');
}

export type RefCheckState =
  | { kind: 'checking' }
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'unchecked' }
  | { kind: 'found'; reason: VerifySymbolSourceReason }
  | { kind: 'candidate'; instrument: EODHDInstrument }
  | { kind: 'missing'; reason: VerifySymbolSourceReason }
  | { kind: 'error' };

/**
 * What is known about the ref currently typed into a field. A verify result only counts if it was
 * made for exactly that ref - after the admin edits the field it is "unchecked" again.
 */
export function refCheckState(
  source: SymbolPriceSource,
  value: string,
  result: VerifySymbolSourceResult | VerifySymbolEODHDResult | null,
  verifying: boolean,
): RefCheckState {
  const ref = normalizeSourceRef(value);
  if (verifying) {
    return { kind: 'checking' };
  }
  if (ref === '') {
    return { kind: 'empty' };
  }
  if (!isValidSourceRef(source, ref)) {
    return { kind: 'invalid' };
  }
  if (result === null) {
    return { kind: 'unchecked' };
  }
  if (result.ref !== null && normalizeSourceRef(result.ref) === ref) {
    if (result.found) {
      return { kind: 'found', reason: result.reason };
    }
    if (result.reason === 'ERROR') {
      return { kind: 'error' };
    }
    if (result.reason === 'INVALID_REF') {
      return { kind: 'invalid' };
    }
    return { kind: 'missing', reason: result.reason };
  }
  if ('candidates' in result) {
    const candidate = result.candidates.find(c => normalizeSourceRef(c.ref) === ref);
    if (candidate !== undefined) {
      return { kind: 'candidate', instrument: candidate };
    }
  }
  return { kind: 'unchecked' };
}

export function refCheckLabel(source: SymbolPriceSource, state: RefCheckState): string {
  switch (state.kind) {
    case 'checking':
      return 'prüfe…';
    case 'empty':
      return 'keine Referenz';
    case 'invalid':
      return `ungültiges Format (${REF_FORMAT_HINTS[source]})`;
    case 'unchecked':
      return 'nicht geprüft';
    case 'found':
    case 'missing':
      return VERIFY_SOURCE_REASON_LABELS[state.reason] ?? state.reason;
    case 'candidate':
      return 'EODHD-Suchtreffer';
    case 'error':
      return VERIFY_SOURCE_REASON_LABELS.ERROR;
  }
}

/** refCheckLabel plus what the verify result knows about the instrument behind the ref. */
export function refCheckDescription(
  source: SymbolPriceSource,
  state: RefCheckState,
  result: VerifySymbolSourceResult | VerifySymbolEODHDResult | null,
  detectionPending: boolean,
): string {
  const label = refCheckLabel(source, state);
  switch (state.kind) {
    case 'empty':
      return detectionPending ? 'wird automatisch ermittelt' : 'keine Referenz – Quelle wird nicht abgefragt';
    case 'found': {
      if (result === null || !('candidates' in result)) {
        return label;
      }
      const details = [result.name, result.currency].filter(v => v !== null && v !== '');
      return details.length > 0 ? `${label} – ${details.join(', ')}` : label;
    }
    case 'candidate':
      return `${label} – ${formatEODHDInstrument(state.instrument)}`;
    default:
      return label;
  }
}

export function formatEODHDInstrument(instrument: EODHDInstrument): string {
  return [instrument.name, instrument.exchange, instrument.currency, instrument.type].filter(v => v !== '').join(', ');
}
